import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LuckyWheel.css';

const DEFAULT_SEGMENTS = [
  { label: 'Preis 1', color: '#FF6B6B', weight: 1 },
  { label: 'Preis 2', color: '#4ECDC4', weight: 1 },
  { label: 'Preis 3', color: '#45B7D1', weight: 1 },
  { label: 'Preis 4', color: '#96CEB4', weight: 1 },
  { label: 'Preis 5', color: '#FFEAA7', weight: 1 },
  { label: 'Preis 6', color: '#DDA0DD', weight: 1 }
];

const LuckyWheel = () => {
  const [segments, setSegments] = useState(DEFAULT_SEGMENTS);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [pressStartTime, setPressStartTime] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  
  const wheelRef = useRef(null);

  // UTF-8 safe encoding/decoding
  const encodeConfig = useCallback((obj) => btoa(unescape(encodeURIComponent(JSON.stringify(obj)))), []);
  const decodeConfig = useCallback((str) => JSON.parse(decodeURIComponent(escape(atob(str)))), []);

  // URL-Konfiguration laden
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const config = urlParams.get('config');
    if (config) {
      try {
        const decoded = decodeConfig(config);
        setSegments(decoded);
      } catch (e) {
        console.error('Fehler beim Laden der Konfiguration:', e);
      }
    }
    
    // History aus LocalStorage laden
    const savedHistory = localStorage.getItem('luckyWheelHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, [decodeConfig]);

  // URL aktualisieren bei Segment-Änderungen
  const updateURL = useCallback((newSegments) => {
    const config = encodeConfig(newSegments);
    const url = new URL(window.location);
    url.searchParams.set('config', config);
    window.history.replaceState({}, '', url);
  }, [encodeConfig]);

  // Segmentwinkel berechnen
  const calculateAngles = useCallback(() => {
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    let currentAngle = 0;
    
    return segments.map(segment => {
      const angle = (segment.weight / totalWeight) * 360;
      const result = { startAngle: currentAngle, endAngle: currentAngle + angle, angle };
      currentAngle += angle;
      return result;
    });
  }, [segments]);

  // Gewinner ermitteln
  const determineWinner = useCallback((finalRotation) => {
    const angles = calculateAngles();
    const normalizedRotation = (360 - (finalRotation % 360)) % 360;
    
    for (let i = 0; i < angles.length; i++) {
      if (normalizedRotation >= angles[i].startAngle && normalizedRotation < angles[i].endAngle) {
        return i;
      }
    }
    return 0;
  }, [calculateAngles]);

  // Rad drehen
  const spinWheel = useCallback((duration, clockwise = Math.random() > 0.5) => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    setWinner(null);
    
    const spinDuration = Math.max(2000, Math.min(6000, duration * 80));
    const spinAmount = 360 * (8 + Math.random() * 12) + Math.random() * 360;
    const finalRotation = rotation + (clockwise ? spinAmount : -spinAmount);
    
    // CSS-Variable für Animation setzen
    if (wheelRef.current) {
      wheelRef.current.style.setProperty('--spin-duration', `${spinDuration}ms`);
      wheelRef.current.style.setProperty('--final-rotation', `${finalRotation}deg`);
    }
    
    setRotation(finalRotation);
    
    setTimeout(() => {
      setIsSpinning(false);
      const winnerIndex = determineWinner(finalRotation);
      setWinner(winnerIndex);
      
      // Gewinner zur History hinzufügen
      const newEntry = {
        segment: segments[winnerIndex],
        timestamp: new Date().toLocaleString('de-DE')
      };
      const newHistory = [newEntry, ...history];
      setHistory(newHistory);
      localStorage.setItem('luckyWheelHistory', JSON.stringify(newHistory));
    }, spinDuration);
  }, [isSpinning, rotation, segments, history, determineWinner]);

  // Tastatur-Events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !pressStartTime && !isSpinning) {
        setPressStartTime(Date.now());
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Enter' && pressStartTime) {
        const duration = Date.now() - pressStartTime;
        setPressStartTime(null);
        spinWheel(duration);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pressStartTime, isSpinning, spinWheel]);

  // Touch-Events
  const handleTouchStart = (e) => {
    if (!isSpinning) {
      const touch = e.touches[0];
      setTouchStart({ x: touch.clientX, y: touch.clientY, time: Date.now() });
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStart) {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      const duration = Date.now() - touchStart.time;
      
      // Bestimme Richtung basierend auf Wischbewegung
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const isClockwise = distance > 30 ? deltaX > 0 : Math.random() > 0.5;
      
      setTouchStart(null);
      spinWheel(duration, isClockwise);
    }
  };

  // Link kopieren
  const copyLink = () => {
    updateURL(segments);
    navigator.clipboard.writeText(window.location.href);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);
  };

  // SVG-Pfad für Segment erstellen
  const createSegmentPath = (startAngle, endAngle, radius) => {
    const start = polarToCartesian(0, 0, radius, endAngle);
    const end = polarToCartesian(0, 0, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return [
      "M", 0, 0,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  // Text-Position berechnen
  const getTextPosition = (startAngle, endAngle, radius) => {
    const angle = (startAngle + endAngle) / 2;
    const textRadius = radius * 0.7;
    return polarToCartesian(0, 0, textRadius, angle);
  };

  const angles = calculateAngles();

  return (
    <div className="lucky-wheel-container">
      {/* Header mit Icons */}
      <div className="header">
        <div className="header-icons">
          <button 
            className="icon-button"
            onClick={() => setShowHistory(true)}
            aria-label="Gewinn-Logbuch öffnen"
          >
            📋
          </button>
          <button 
            className="icon-button share-button"
            onClick={copyLink}
            aria-label="Link teilen"
          >
            🔗
            {showTooltip && <span className="tooltip">Link kopiert!</span>}
          </button>
          <button 
            className="icon-button"
            onClick={() => setShowConfig(true)}
            aria-label="Einstellungen öffnen"
          >
            ⚙️
          </button>
        </div>
      </div>

      {/* Hauptbereich */}
      <div className="wheel-area">
        {/* Zeiger */}
        <div className="pointer">▼</div>
        
        {/* Glücksrad */}
        <div 
          className={`wheel ${isSpinning ? 'spinning' : ''}`}
          style={{ transform: `rotate(${rotation}deg)` }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          ref={wheelRef}
        >
          <svg width="400" height="400" viewBox="-200 -200 400 400">
            {segments.map((segment, index) => {
              const angle = angles[index];
              const path = createSegmentPath(angle.startAngle, angle.endAngle, 190);
              const textPos = getTextPosition(angle.startAngle, angle.endAngle, 190);
              const textAngle = (angle.startAngle + angle.endAngle) / 2;
              
              return (
                <g key={index}>
                  <path
                    d={path}
                    fill={segment.color}
                    stroke="#fff"
                    strokeWidth="2"
                  />
                  <text
                    x={textPos.x}
                    y={textPos.y}
                    fill="#000"
                    fontSize="16"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${textAngle > 90 && textAngle < 270 ? textAngle + 180 : textAngle}, ${textPos.x}, ${textPos.y})`}
                  >
                    {segment.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Anweisungen */}
        <div className="instructions">
          <p>Drücke Enter oder berühre das Rad zum Drehen</p>
          <p>Je länger du drückst, desto stärker dreht es sich!</p>
        </div>

      </div>

      {/* Gewinner-Anzeige am unteren Rand */}
      {winner !== null && (
        <div className="winner-display">
          🎉 Gewinner: <strong>{segments[winner].label}</strong> 🎉
        </div>
      )}

      {/* Konfigurations-Popup */}
      {showConfig && (
        <ConfigModal 
          segments={segments}
          onSave={(newSegments) => {
            setSegments(newSegments);
            updateURL(newSegments);
            setShowConfig(false);
          }}
          onClose={() => setShowConfig(false)}
        />
      )}

      {/* History-Popup */}
      {showHistory && (
        <HistoryModal 
          history={history}
          onClear={() => {
            setHistory([]);
            localStorage.removeItem('luckyWheelHistory');
          }}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

// Konfigurations-Modal
const ConfigModal = ({ segments, onSave, onClose }) => {
  const [editSegments, setEditSegments] = useState([...segments]);

  const updateSegment = (index, field, value) => {
    const updated = [...editSegments];
    updated[index] = { ...updated[index], [field]: value };
    setEditSegments(updated);
  };

  const addSegment = () => {
    setEditSegments([...editSegments, { label: 'Neues Segment', color: '#FF6B6B', weight: 1 }]);
  };

  const removeSegment = (index) => {
    if (editSegments.length > 2) {
      setEditSegments(editSegments.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Rad konfigurieren</h2>
        <div className="segments-config">
          {editSegments.map((segment, index) => (
            <div key={index} className="segment-config">
              <input
                type="text"
                value={segment.label}
                onChange={(e) => updateSegment(index, 'label', e.target.value)}
                placeholder="Label"
              />
              <input
                type="color"
                value={segment.color}
                onChange={(e) => updateSegment(index, 'color', e.target.value)}
              />
              <input
                type="number"
                min="1"
                value={segment.weight}
                onChange={(e) => updateSegment(index, 'weight', parseInt(e.target.value) || 1)}
                placeholder="Gewicht"
              />
              {editSegments.length > 2 && (
                <button onClick={() => removeSegment(index)} className="remove-btn">❌</button>
              )}
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button onClick={addSegment} className="add-btn">+ Segment hinzufügen</button>
          <button onClick={() => onSave(editSegments)} className="save-btn">Speichern</button>
          <button onClick={onClose} className="cancel-btn">Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

// History-Modal
const HistoryModal = ({ history, onClear, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Gewinn-Logbuch</h2>
        <div className="history-list">
          {history.length === 0 ? (
            <p>Noch keine Gewinne verzeichnet.</p>
          ) : (
            history.map((entry, index) => (
              <div key={index} className="history-entry">
                <span className="winner-label" style={{ color: entry.segment.color }}>
                  {entry.segment.label}
                </span>
                <span className="timestamp">{entry.timestamp}</span>
              </div>
            ))
          )}
        </div>
        <div className="modal-actions">
          {history.length > 0 && (
            <button onClick={onClear} className="clear-btn">Logbuch leeren</button>
          )}
          <button onClick={onClose} className="cancel-btn">Schließen</button>
        </div>
      </div>
    </div>
  );
};

export default LuckyWheel;